/**
 * CrmBoard — pipeline de Leads (/orcamentos index).
 *
 * O CRM e um board kanban onde cada card e um LEAD e cada coluna e uma ETAPA
 * configuravel. "Gerar orcamento" e apenas uma etapa (tipo ORCAMENTO).
 *
 * Estrutura (3 zonas, fiel ao mockup Fase 3):
 *   - Cabecalho: breadcrumb + titulo "CRM" + metricas + toolbar
 *     (toggle Tabela/Kanban, Configurar pipeline, Exportar, "Novo lead")
 *   - Barra de filtros: busca + "Filtros [N]" + segmentado Todos/Meus
 *     + chips ATIVOS
 *   - Board .kanban (overflow-x permitido — unica excecao ao zero-scroll) com
 *     uma EtapaColuna por coluna; OU modo Tabela (lista flat).
 *
 * Drag entre colunas: HTML5 nativo (mesma mecanica de Kanbans/Board.jsx) —
 * update otimista + reload/rollback. Semantica nova (itens 12/13): mover para
 * qualquer etapa e direto, sem campos extras. FINALIZADO converte o lead em
 * cliente no backend; PARADO e apenas uma pausa. Nao ha motivo de perda.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import PeriodoEntradaFiltro from "./PeriodoEntradaFiltro";
import {
  PERIODO_PERSONALIZADO,
  intervaloDoPeriodo,
  labelDoPeriodo,
  rotuloIntervaloLivre,
} from "./periodoEntrada";
import {
  FiAlertCircle,
  FiChevronDown,
  FiFilter,
  FiPlus,
  FiSearch,
  FiSettings,
} from "react-icons/fi";

import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  configurarPipeline,
  criarLead,
  exportarLeads,
  listarBoard,
  moverLead,
  obterPipeline,
  obterPipelineValor,
  ORIGEM_LEAD_LABEL,
  reordenarEtapas,
  SEM_RESPONSAVEL,
} from "../../services/crm";
import { listarEtapasEmUso } from "../../services/formularios";
import { fmtMoeda } from "../Orcamentos/helpers";
import {
  moverTemEfeito,
  podeConfigurarPipeline,
  podeCriarLead,
  podeMoverLead,
} from "../../utils/crmPermissoes";

import ConfigurarPipelinePanel from "./ConfigurarPipelinePanel";
import EtapaColuna from "./EtapaColuna";
import { destinoDaTabela } from "./moverNaTabela";
import ExportMenu from "./ExportMenu";
import useResponsaveisCrm from "../../hooks/useResponsaveisCrm";
import FiltrosPanel from "./FiltrosPanel";
import KanbanSkeleton, { Ghost as SkeletonBar } from "./KanbanSkeleton";
import NovoLeadForm from "./NovoLeadForm";
import useKanbanAutoScroll from "./useKanbanAutoScroll";
import { exportarCrm } from "./crmHelpers";
import { Card, EmptyMsg, ErrorMsg, Table, TableWrapper } from "../Orcamentos/styles";

// Largura FIXA das colunas do board por preferencia (Configurar pipeline).
// Fixa = independe do conteudo dos cards (refinamento REF-1). Persistida no
// backend em ConfiguracaoComercial.pipeline_largura_coluna.
const LARGURA_COLUNA_PX = { PEQUENA: 248, MEDIA: 288, GRANDE: 340 };

// Preferencias de visualizacao das colunas (REF-7): recolhidas/ocultas sao
// estado de view do usuario (nao do dominio) — persistidas em localStorage.
const LS_COLAPSADAS = "crm:colunas:colapsadas";
const LS_OCULTAS = "crm:colunas:ocultas";
const LS_GRUPOS_COLAPSADOS = "crm:grupos:colapsados";

// Converte uma cor hex (#RGB ou #RRGGBB) num rgba esmaecido, usado como fundo
// "lane" dos grupos do board (estilo ClickUp). Retorna null se nao for hex
// (ex.: "var(--ink-3)" do grupo-fallback "Sem grupo") — o caller cai no neutro.
function corEsmaecida(cor, alpha) {
  const hex = String(cor || "").trim().replace(/^#/, "");
  const full =
    hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return null;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function lerSetLS(key) {
  try {
    const arr = JSON.parse(window.localStorage.getItem(key) || "[]");
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function salvarSetLS(key, set) {
  try {
    window.localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* sem storage */
  }
}

function CrmBoard() {
  const { request, isMaster, hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [colunas, setColunas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // R$ do cabecalho pela FONTE configurada (painel comercial — Fase 5).
  // Buscado em paralelo ao board (sem bloquear). `null` = endpoint indisponivel
  // -> o cabecalho cai no fallback (Σ valor_estimado somado no frontend).
  const [pipelineFonte, setPipelineFonte] = useState(null);

  const [filtros, setFiltros] = useState({
    busca: "",
    etapa: "",
    responsavel: "",
    origem: "",
    meus: false,
    // Periodo de ENTRADA NA ETAPA: guardamos o preset (para a UI) e o
    // intervalo ja resolvido (o que vai na query). Resolver aqui, e nao no
    // service, mantem o calculo no fuso do usuario.
    entrouPeriodo: "",
    entrou_de: "",
    entrou_ate: "",
  });
  const [periodoAberto, setPeriodoAberto] = useState(false);
  const [seg, setSeg] = useState("todos"); // todos | meus
  const [viewMode, setViewMode] = useState("kanban"); // kanban | tabela

  // Busca com debounce (perf): o input atualiza `buscaInput` na hora, mas so
  // propaga para `filtros.busca` (que dispara a query do board) 350ms apos
  // parar de digitar — evita 1 request por tecla.
  const [buscaInput, setBuscaInput] = useState("");

  // Colunas recolhidas / ocultas (REF-7) — view-state persistido em localStorage.
  const [colapsadas, setColapsadas] = useState(() => lerSetLS(LS_COLAPSADAS));
  const [ocultas, setOcultas] = useState(() => lerSetLS(LS_OCULTAS));
  // Grupos recolhidos (Fase D) — view-state em localStorage (igual colunas).
  const [gruposColapsados, setGruposColapsados] = useState(() =>
    lerSetLS(LS_GRUPOS_COLAPSADOS)
  );

  const toggleGrupoColapsado = useCallback((grupoId) => {
    setGruposColapsados((prev) => {
      const proximo = new Set(prev);
      const key = String(grupoId);
      if (proximo.has(key)) proximo.delete(key);
      else proximo.add(key);
      salvarSetLS(LS_GRUPOS_COLAPSADOS, proximo);
      return proximo;
    });
  }, []);

  // Regras do pipeline (governam podeMover) + pipeline completo p/ config.
  const [pipeline, setPipeline] = useState({ etapas: [], regras: {} });
  // Formularios do site por coluna: o aviso de Configurar pipeline antes de
  // remover a coluna de destino deles (FORM-01).
  const [formulariosPorEtapa, setFormulariosPorEtapa] = useState({});

  // Paineis / dialogos
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  // `/crm?configurar=pipeline` abre direto o painel (link da lista "Configure sua empresa").
  const [searchParams] = useSearchParams();
  const [configAberto, setConfigAberto] = useState(() => searchParams.get("configurar") === "pipeline");
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  const [novoLead, setNovoLead] = useState(null); // {etapa?} | null
  const [salvandoLead, setSalvandoLead] = useState(false);

  // Drag state (HTML5 nativo)
  const [dragLeadId, setDragLeadId] = useState(null);
  const [dragOverEtapaId, setDragOverEtapaId] = useState(null);

  // Auto-scroll lateral do board durante o drag (Fase B): o DnD nativo nao
  // rola sozinho ao chegar na borda. O hook escuta o dragover no container.
  const kanbanRef = useRef(null);
  const handleBoardDragOver = useKanbanAutoScroll(kanbanRef, Boolean(dragLeadId));

  const regras = pipeline.regras || {};
  // CRM-23: cada gate espelha o portao do backend (utils/crmPermissoes). O
  // board olhava so a regra "vendedor pode mover", sem permissao nenhuma:
  // gestor nao arrastava com a regra desligada e quem so via o lead
  // arrastava e levava 403.
  const podeMover = podeMoverLead(hasPermission, regras, isMaster);
  const podeConfigurar = podeConfigurarPipeline(hasPermission, isMaster);
  const podeAdicionar = podeCriarLead(hasPermission, isMaster);
  const larguraColunaPx =
    LARGURA_COLUNA_PX[regras.pipeline_largura_coluna] || LARGURA_COLUNA_PX.MEDIA;

  // ---------- Carregar pipeline (regras + etapas) ----------
  const carregarPipeline = useCallback(async () => {
    const data = await obterPipeline(request);
    setPipeline(data);
  }, [request]);

  useEffect(() => {
    carregarPipeline();
  }, [carregarPipeline]);

  // Quantos formularios do site mandam leads para cada coluna. Exige
  // `pipeline:configurar`, a mesma permissao do painel — quem nao configura o
  // pipeline nao precisa da lista. Falhar aqui so tira o aviso: o painel
  // continua funcionando como antes.
  const carregarEtapasEmUso = useCallback(async () => {
    if (!podeConfigurar) return;
    try {
      const data = await listarEtapasEmUso(request, { silentError: true });
      setFormulariosPorEtapa(data?.etapas || {});
    } catch (_) {
      setFormulariosPorEtapa({});
    }
  }, [request, podeConfigurar]);

  useEffect(() => {
    carregarEtapasEmUso();
  }, [carregarEtapasEmUso]);

  // ---------- Carregar board ----------
  const carregar = useCallback(async (opts) => {
    // `silent`: reconcilia o board sem mostrar o estado de loading (usado
    // depois de um movimento otimista — evita flicker).
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    // R$ do cabecalho pela fonte configurada, com os MESMOS filtros do board.
    // Em paralelo: nao bloqueia o board e cai em null (fallback) se falhar.
    obterPipelineValor(request, filtros).then(setPipelineFonte);
    try {
      const data = await listarBoard(request, filtros);
      setColunas(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err?.message || "Erro ao carregar o pipeline.");
      setColunas([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [request, filtros]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Propaga a busca digitada para os filtros, com debounce.
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltros((prev) =>
        prev.busca === buscaInput ? prev : { ...prev, busca: buscaInput }
      );
    }, 350);
    return () => clearTimeout(t);
  }, [buscaInput]);

  // ---------- Metricas (cabecalho) ----------
  // `total` (contagem) e `etapas` vem sempre das colunas do board. `soma` (R$)
  // usa a FONTE configurada (pipelineFonte, somada no backend sobre os mesmos
  // leads) quando disponivel; senao cai no fallback Σ valor_estimado local —
  // sem quebrar se o endpoint falhar/retornar null (regressao-zero).
  // B1 (auditoria 2026-08-07): o total do cabecalho vinha da FONTE
  // configurada e cada card mostrava `valor_estimado` cru. As duas reguas
  // nunca fechavam — somar os cards na mao dava outro numero, e nada na
  // tela dizia por que. O backend agora manda `valor_pipeline` por lead
  // (mesma regra do total), entao o fallback local soma a MESMA coisa.
  const metricas = useMemo(() => {
    let total = 0;
    let somaFallback = 0;
    colunas.forEach((col) => {
      const leads = col.leads || [];
      total += leads.length;
      leads.forEach((l) => {
        somaFallback += Number(l.valor_pipeline ?? l.valor_estimado) || 0;
      });
    });
    const soma = pipelineFonte ? Number(pipelineFonte.soma) : somaFallback;
    return { total, soma, etapas: colunas.length };
  }, [colunas, pipelineFonte]);

  // ---------- Grupos de status (Fase D) ----------
  // Agrupado quando ha grupos no pipeline E as colunas referenciam grupo
  // (o backend garante tudo-ou-nada). Senao, board plano (como antes).
  const gruposDoPipeline = useMemo(() => pipeline.grupos || [], [pipeline]);
  const agrupado = useMemo(
    () => gruposDoPipeline.length > 0 && colunas.some((c) => c.etapa?.grupo),
    [gruposDoPipeline, colunas]
  );
  const colunasPorGrupo = useMemo(() => {
    const info = new Map(gruposDoPipeline.map((g) => [String(g.id), g]));
    const byGrupo = new Map();
    colunas
      .filter((col) => !ocultas.has(col.etapa?.id))
      .forEach((col) => {
        const gid = col.etapa?.grupo ? String(col.etapa.grupo) : "__sem__";
        if (!byGrupo.has(gid)) {
          byGrupo.set(gid, { id: gid, grupo: info.get(gid) || null, colunas: [] });
        }
        byGrupo.get(gid).colunas.push(col);
      });
    return [...byGrupo.values()].sort(
      (a, b) => (a.grupo?.ordem ?? 999) - (b.grupo?.ordem ?? 999)
    );
  }, [colunas, ocultas, gruposDoPipeline]);

  // ---------- Listas derivadas para filtros (sem endpoint extra) ----------
  const etapasParaFiltro = useMemo(
    () => colunas.map((c) => c.etapa).filter(Boolean),
    [colunas]
  );

  // CRM-22: responsaveis pelo endpoint do CRM (usuarios ativos da unidade),
  // nao mais derivados dos cards — vendedor novo sem lead aparece, e o
  // filtro por "Ana" nao encolhe a lista para so a Ana.
  const { responsaveis } = useResponsaveisCrm();

  // ---------- Filtros ----------
  const aplicarFiltro = (chave, valor) => {
    setFiltros((prev) => ({ ...prev, [chave]: valor }));
  };

  // Um unico periodo vale para TODAS as colunas (o menu de cada coluna chama
  // esta mesma funcao): comparar colunas exige a mesma janela, e janela por
  // coluna seria estado invisivel no quadro.
  const aplicarPeriodoEntrada = useCallback((preset) => {
    const { de, ate } = intervaloDoPeriodo(preset);
    setFiltros((prev) => ({
      ...prev, entrouPeriodo: preset, entrou_de: de, entrou_ate: ate,
    }));
  }, []);

  // Intervalo escolhido a dedo. Um lado vazio e valido ("a partir de X");
  // com os dois vazios o filtro simplesmente sai.
  const aplicarIntervaloEntrada = useCallback(({ de, ate }) => {
    setFiltros((prev) => ({
      ...prev,
      entrouPeriodo: de || ate ? PERIODO_PERSONALIZADO : "",
      entrou_de: de || "",
      entrou_ate: ate || "",
    }));
  }, []);

  // ---------- Acoes do menu de coluna (REF-7) ----------
  const toggleRecolher = useCallback((etapa) => {
    setColapsadas((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(etapa.id)) proximo.delete(etapa.id);
      else proximo.add(etapa.id);
      salvarSetLS(LS_COLAPSADAS, proximo);
      return proximo;
    });
  }, []);

  const ocultarColuna = useCallback((etapa) => {
    setOcultas((prev) => {
      const proximo = new Set(prev);
      proximo.add(etapa.id);
      salvarSetLS(LS_OCULTAS, proximo);
      return proximo;
    });
  }, []);

  const restaurarColunas = useCallback(() => {
    setOcultas(() => {
      salvarSetLS(LS_OCULTAS, new Set());
      return new Set();
    });
  }, []);

  const moverColuna = useCallback(
    async (etapa, dir) => {
      const ordem = colunas.map((c) => c.etapa?.id).filter(Boolean);
      const idx = ordem.indexOf(etapa.id);
      const alvo = dir === "esq" ? idx - 1 : idx + 1;
      if (idx < 0 || alvo < 0 || alvo >= ordem.length) return;
      const nova = [...ordem];
      [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
      // Otimista: reordena as colunas na UI antes de persistir.
      setColunas((prev) => {
        const porId = new Map(prev.map((c) => [c.etapa?.id, c]));
        return nova.map((id) => porId.get(id)).filter(Boolean);
      });
      try {
        await reordenarEtapas(request, nova);
        await carregarPipeline();
      } catch {
        toast.error("Não foi possível reordenar a coluna.");
        carregar();
      }
    },
    [colunas, request, carregarPipeline, carregar, toast]
  );

  const selecionarSeg = (valor) => {
    setSeg(valor);
    // "meus" mapeia para a flag ?meus=true (backend resolve por request.user).
    setFiltros((prev) => ({ ...prev, meus: valor === "meus" }));
  };

  const limparFiltros = () => {
    setBuscaInput("");
    setFiltros({
      busca: "", etapa: "", responsavel: "", origem: "", meus: false,
      entrouPeriodo: "", entrou_de: "", entrou_ate: "",
    });
    setSeg("todos");
  };

  // Chips de filtros ativos (etapa/responsavel/origem/busca).
  const chipsAtivos = useMemo(() => {
    const out = [];
    if (filtros.busca) out.push({ key: "busca", label: `"${filtros.busca}"` });
    if (filtros.etapa) {
      const et = etapasParaFiltro.find((e) => String(e.id) === String(filtros.etapa));
      out.push({ key: "etapa", label: et ? et.nome : "Etapa" });
    }
    if (filtros.responsavel === SEM_RESPONSAVEL) {
      out.push({ key: "responsavel", label: "Sem responsável" });
    } else if (filtros.responsavel) {
      const r = responsaveis.find((x) => String(x.id) === String(filtros.responsavel));
      out.push({ key: "responsavel", label: r ? r.nome : "Responsável" });
    }
    // O chip mostra o rotulo ("Indicação"), nao o codigo ("INDICACAO").
    if (filtros.origem) {
      out.push({ key: "origem", label: ORIGEM_LEAD_LABEL[filtros.origem] || filtros.origem });
    }
    if (filtros.entrouPeriodo) {
      const rotulo = filtros.entrouPeriodo === PERIODO_PERSONALIZADO
        ? rotuloIntervaloLivre(filtros.entrou_de, filtros.entrou_ate)
        : labelDoPeriodo(filtros.entrouPeriodo).toLowerCase();
      out.push({ key: "entrouPeriodo", label: `Entrou na etapa: ${rotulo}` });
    }
    return out;
  }, [filtros, etapasParaFiltro, responsaveis]);

  const nFiltrosAvancados = [filtros.etapa, filtros.responsavel, filtros.origem].filter(
    Boolean
  ).length;

  // ---------- Drag handlers (HTML5 nativo) ----------
  const handleDragStart = (e, lead) => {
    if (!podeMover) return;
    setDragLeadId(lead.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", lead.id);
  };

  const handleDragEnd = () => {
    setDragLeadId(null);
    setDragOverEtapaId(null);
  };

  const handleDragOver = (e, etapa) => {
    if (!dragLeadId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverEtapaId !== etapa.id) setDragOverEtapaId(etapa.id);
  };

  const handleDragLeave = () => setDragOverEtapaId(null);

  // Aplica o movimento otimista (move o lead para a coluna destino na UI).
  const moverOtimista = useCallback((leadId, etapaDestinoId) => {
    setColunas((prev) => {
      let leadMovido = null;
      const semLead = prev.map((col) => {
        const restantes = (col.leads || []).filter((l) => {
          if (l.id === leadId) {
            leadMovido = l;
            return false;
          }
          return true;
        });
        return { ...col, leads: restantes };
      });
      if (!leadMovido) return prev;
      return semLead.map((col) =>
        col.etapa?.id === etapaDestinoId
          ? { ...col, leads: [{ ...leadMovido, etapa: etapaDestinoId }, ...(col.leads || [])] }
          : col
      );
    });
  }, []);

  // Executa a transicao no backend + reload. Semantica nova: nenhuma etapa
  // exige campos extras (FINALIZADO converte em cliente; PARADO so pausa).
  const executarMover = useCallback(
    async (lead, etapaDestino) => {
      moverOtimista(lead.id, etapaDestino.id);
      try {
        await moverLead(request, lead.id, { etapa_destino: etapaDestino.id });
        // Movimento normal confia no otimista — SEM reload (o card muda de
        // coluna na hora). So a etapa FINALIZADO converte o lead em cliente no
        // backend (ele some do board), entao ai reconciliamos em silencio.
        if (etapaDestino?.tipo === "FINALIZADO") {
          await carregar({ silent: true });
        }
      } catch (err) {
        // Backend rejeitou (ex.: gate de permissao). Auto-toast ja
        // disparou (request); reverte via reload silencioso.
        setError(err?.message || "Não foi possível mover o lead.");
        await carregar({ silent: true });
      }
    },
    [request, moverOtimista, carregar]
  );

  const handleDrop = (e, etapaDestino) => {
    e.preventDefault();
    const leadId = dragLeadId || e.dataTransfer.getData("text/plain");
    handleDragEnd();
    if (!leadId) return;

    // Localiza o lead e a etapa de origem.
    let lead = null;
    let origemId = null;
    colunas.forEach((col) => {
      (col.leads || []).forEach((l) => {
        if (l.id === leadId) {
          lead = l;
          origemId = col.etapa?.id;
        }
      });
    });
    // Soltar na propria coluna nao faz nada — exceto na coluna de conversao
    // com o lead ainda sem ganho (FORM-20): o backend aplica o ganho.
    if (!lead || !moverTemEfeito(origemId, etapaDestino, lead)) return;

    // Move direto — nenhuma etapa exige campos extras (semantica nova).
    executarMover(lead, etapaDestino);
  };

  // ---------- Acoes de lead ----------
  const handleCriarLead = async (payload) => {
    setSalvandoLead(true);
    try {
      // Se o form foi aberto por uma coluna, fixa a etapa de entrada.
      const corpo = { ...payload };
      if (novoLead?.etapa?.id) corpo.etapa = novoLead.etapa.id;
      // `silentError`: o form mostra o erro no campo; o toast so repetiria.
      await criarLead(request, corpo, { silentError: true });
      setNovoLead(null);
      toast.success("Lead criado.");
      await carregar();
    } finally {
      setSalvandoLead(false);
    }
  };

  // Clicar num card abre o Cockpit do Lead (tela cheia) — substitui o antigo
  // SlideOverPanel raso. A rota vive no shell de /crm (index.jsx).
  const abrirLead = useCallback(
    (lead) => {
      navigate(`/crm/leads/${lead.id}`);
    },
    [navigate]
  );

  // ---------- Configurar pipeline ----------
  const handleSalvarPipeline = async ({
    etapas,
    grupos,
    regras: novasRegras,
    remapeamentos,
  }) => {
    setSalvandoConfig(true);
    try {
      const resp = await configurarPipeline(request, {
        etapas,
        grupos,
        regras: novasRegras,
        remapeamentos,
      });
      const remapeados = resp?.leads_remapeados ?? 0;
      toast.success(
        remapeados > 0
          ? `Pipeline salvo. ${remapeados} lead(s) remapeado(s).`
          : "Pipeline salvo."
      );
      setConfigAberto(false);
      await carregarPipeline();
      await carregarEtapasEmUso();
      await carregar();
    } finally {
      setSalvandoConfig(false);
    }
  };

  // ---------- Exportar ----------
  // As linhas vem de /leads/exportar com os MESMOS filtros do board: o board
  // e leve (sem empresa, endereco, telefone 2, bio, parceiros) e a planilha
  // promete o lead inteiro (CRM-35).
  const handleExportar = async (formato) => {
    try {
      const leads = await exportarLeads(request, filtros);
      exportarCrm(formato, { leads: Array.isArray(leads) ? leads : [], metricas });
    } catch (err) {
      toast.error(err?.message || "Falha ao exportar.");
    }
  };

  // ---------- Render ----------
  // Tela full-bleed (item 8): ocupa 100% da viewport abaixo da topbar. O
  // cabecalho + filtros ficam no topo (altura natural) e o board (.kanban)
  // ocupa a altura restante, rolando verticalmente dentro das colunas. Por
  // isso NAO usamos PageContainer (que aplica --page-max-width + padding).
  // Render de uma coluna (reusado no modo plano e agrupado).
  const renderColuna = (col) => {
    const idxFull = colunas.findIndex((c) => c.etapa?.id === col.etapa?.id);
    return (
      <EtapaColuna
        key={col.etapa?.id}
        etapa={col.etapa}
        leads={col.leads || []}
        podeMover={podeMover}
        podeAdicionar={podeAdicionar}
        podeConfigurar={podeConfigurar}
        dragLeadId={dragLeadId}
        dragOver={dragOverEtapaId === col.etapa?.id}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onAbrirLead={abrirLead}
        onAdicionar={(etapa) => setNovoLead({ etapa })}
        onConfigurar={() => setConfigAberto(true)}
        colapsada={colapsadas.has(col.etapa?.id)}
        onRecolher={toggleRecolher}
        onOcultar={ocultarColuna}
        onFiltrar={(etapa) => aplicarFiltro("etapa", etapa.id)}
        periodoEntrada={filtros.entrouPeriodo}
        onPeriodoEntrada={aplicarPeriodoEntrada}
        onAbrirIntervalo={() => setPeriodoAberto(true)}
        onMover={moverColuna}
        podeMoverEsq={idxFull > 0}
        podeMoverDir={idxFull < colunas.length - 1}
      />
    );
  };

  return (
    <div className="crm-board" style={{ "--crm-col-w": `${larguraColunaPx}px` }}>
      <div className="crm-board__top">
      {/* ---- Cabecalho CRM ---- */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="breadcrumb">
            <span>Início</span>
            <span className="crumb-sep">/</span>
            <span>Comercial</span>
            <span className="crumb-sep">/</span>
            <span className="active">CRM · Pipeline</span>
          </div>
          <h1 className="page-title">CRM</h1>
          <div className="page-sub">
            {metricas.total} leads no pipeline · {fmtMoeda(metricas.soma)} ·{" "}
            {podeConfigurar ? (
              <button
                type="button"
                onClick={() => setConfigAberto(true)}
                style={{
                  background: "none",
                  border: 0,
                  padding: 0,
                  color: "var(--accent)",
                  fontWeight: 500,
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                {metricas.etapas} etapas configuradas
              </button>
            ) : (
              <span>{metricas.etapas} etapas</span>
            )}
          </div>
        </div>

        <div className="flex gap-2 items-center" style={{ flexWrap: "wrap" }}>
          <div className="tabchip-group">
            <div
              className={"tabchip" + (viewMode === "tabela" ? " active" : "")}
              onClick={() => setViewMode("tabela")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setViewMode("tabela")}
            >
              Tabela
            </div>
            <div
              className={"tabchip" + (viewMode === "kanban" ? " active" : "")}
              onClick={() => setViewMode("kanban")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setViewMode("kanban")}
            >
              Kanban
            </div>
          </div>
          {podeConfigurar && (
            <button
              className="btn btn--ghost"
              type="button"
              onClick={() => setConfigAberto(true)}
            >
              <FiSettings size={15} aria-hidden="true" /> Configurar pipeline
            </button>
          )}
          <ExportMenu onExportar={handleExportar} disabled={metricas.total === 0} />
          {podeAdicionar && (
            <button
              className="btn btn--primary"
              type="button"
              onClick={() => setNovoLead({})}
            >
              <FiPlus size={14} aria-hidden="true" /> Novo lead
            </button>
          )}
        </div>
      </div>

      {/* ---- Barra de filtros ---- */}
      <div className="flex gap-2 items-center" style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <div className="input-prefixed">
          <FiSearch size={14} aria-hidden="true" />
          <input
            type="search"
            placeholder="Buscar por título, contato, telefone, e-mail, empresa, número..."
            value={buscaInput}
            onChange={(e) => setBuscaInput(e.target.value)}
          />
        </div>
        <button
          className="btn btn--secondary btn--sm"
          type="button"
          onClick={() => setFiltrosAbertos(true)}
        >
          <FiFilter size={14} aria-hidden="true" /> Filtros
          {nFiltrosAvancados > 0 && (
            <span className="badge accent no-dot">{nFiltrosAvancados}</span>
          )}
        </button>
        <PeriodoEntradaFiltro
          valor={filtros.entrouPeriodo}
          de={filtros.entrou_de}
          ate={filtros.entrou_ate}
          onChange={aplicarPeriodoEntrada}
          onIntervalo={aplicarIntervaloEntrada}
          aberto={periodoAberto}
          onAbertoChange={setPeriodoAberto}
        />
        <div className="tabchip-group">
          {["todos", "meus"].map((s) => (
            <div
              key={s}
              className={"tabchip" + (seg === s ? " active" : "")}
              onClick={() => selecionarSeg(s)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && selecionarSeg(s)}
            >
              {s === "todos" ? "Todos" : "Meus"}
            </div>
          ))}
        </div>
        {chipsAtivos.length > 0 && (
          <div className="fp__active-row">
            <span className="lbl">ATIVOS</span>
            {chipsAtivos.map((f) => (
              <span className="fp__active-chip" key={f.key}>
                {f.label}
                <button
                  type="button"
                  onClick={() => {
                    if (f.key === "busca") setBuscaInput("");
                    // O periodo guarda preset + intervalo resolvido: limpar so
                    // o preset apagaria o rotulo mas manteria as datas na
                    // query — quadro filtrado sem nada na tela dizendo isso.
                    else if (f.key === "entrouPeriodo") aplicarPeriodoEntrada("");
                    else aplicarFiltro(f.key, f.key === "meus" ? false : "");
                  }}
                  aria-label="Remover filtro"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {error && (
        <ErrorMsg>
          <FiAlertCircle size={14} aria-hidden="true" /> {error}
        </ErrorMsg>
      )}
      </div>

      {/* ---- Conteudo: Kanban (default) ou Tabela ---- */}
      <div
        className={
          "crm-board__main" + (viewMode === "tabela" ? " crm-board__main--scroll" : "")
        }
      >
      {viewMode === "kanban" ? (
        <>
          {ocultas.size > 0 && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                padding: "0 0 8px",
              }}
            >
              <span style={{ fontSize: "0.78rem", color: "var(--ink-3)" }}>
                {ocultas.size} coluna(s) oculta(s)
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={restaurarColunas}
              >
                Mostrar todas
              </button>
            </div>
          )}
          {loading && colunas.length === 0 ? (
            <KanbanSkeleton colunas={6} />
          ) : (
            <div className="kanban" ref={kanbanRef} onDragOver={handleBoardDragOver}>
              {agrupado
                ? colunasPorGrupo.map((bloco) => {
                    const colaps = gruposColapsados.has(bloco.id);
                    const cor = bloco.grupo?.cor || "var(--ink-3)";
                    // Fundo esmaecido na cor do grupo (lane estilo ClickUp):
                    // bem sutil — banda um tico mais visivel, lane quase
                    // transparente pra nao competir com os cards.
                    const tintLane = corEsmaecida(cor, 0.035);
                    const tintBanda = corEsmaecida(cor, 0.09);
                    const tintBorda = corEsmaecida(cor, 0.12);
                    const totLeads = bloco.colunas.reduce(
                      (s, c) => s + (c.leads?.length || 0),
                      0
                    );
                    // B1: a banda do grupo somava `valor_estimado` enquanto
                    // o cabecalho da tela somava pela fonte configurada — dois
                    // totais do MESMO board, sem dizer que regua cada um usa.
                    // `valor_pipeline` vem do backend com a regra do cabecalho.
                    const totValor = bloco.colunas.reduce(
                      (s, c) =>
                        s +
                        (c.leads || []).reduce(
                          (sl, l) =>
                            sl + (Number(l.valor_pipeline ?? l.valor_estimado) || 0),
                          0
                        ),
                      0
                    );
                    return (
                      <div
                        key={bloco.id}
                        className="crm-grupo"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                          padding: "6px 6px 10px",
                          borderRadius: 12,
                          background: tintLane || "transparent",
                          border: `1px solid ${tintBorda || "transparent"}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 10px",
                            borderTop: `3px solid ${cor}`,
                            borderRadius: 8,
                            background: tintBanda || "var(--surface-2)",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => toggleGrupoColapsado(bloco.id)}
                            aria-label={colaps ? "Expandir grupo" : "Recolher grupo"}
                            title={colaps ? "Expandir grupo" : "Recolher grupo"}
                            style={{
                              border: 0,
                              background: "transparent",
                              cursor: "pointer",
                              display: "grid",
                              placeItems: "center",
                              padding: 0,
                              color: "var(--ink-2)",
                            }}
                          >
                            <FiChevronDown
                              size={15}
                              style={{
                                transform: colaps ? "rotate(-90deg)" : "none",
                                transition: "transform .15s",
                              }}
                              aria-hidden="true"
                            />
                          </button>
                          <span
                            style={{ width: 9, height: 9, borderRadius: 3, background: cor }}
                            aria-hidden="true"
                          />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                            {bloco.grupo?.nome || "Sem grupo"}
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              color: "var(--ink-3)",
                              marginLeft: "auto",
                            }}
                          >
                            {totLeads} {totLeads === 1 ? "negócio" : "negócios"} ·{" "}
                            {fmtMoeda(totValor)}
                          </span>
                        </div>
                        {!colaps && (
                          <div className="crm-grupo__cols">
                            {bloco.colunas.map(renderColuna)}
                          </div>
                        )}
                      </div>
                    );
                  })
                : colunas
                    .filter((col) => !ocultas.has(col.etapa?.id))
                    .map(renderColuna)}
            </div>
          )}
        </>
      ) : (
        <CrmTabela
          colunas={colunas}
          loading={loading}
          onAbrirLead={abrirLead}
          etapas={etapasParaFiltro}
          podeMover={podeMover}
          onMoverEtapa={(lead, etapaId) => {
            // Mesma regra do arraste (handleDrop): a propria coluna de
            // conversao ainda move quando falta o ganho (FORM-20).
            const dest = destinoDaTabela(colunas, lead, etapaId);
            if (dest) executarMover(lead, dest);
          }}
        />
      )}
      </div>

      {/* ---- Paineis ---- */}
      <FiltrosPanel
        open={filtrosAbertos}
        onClose={() => setFiltrosAbertos(false)}
        filtros={filtros}
        onAplicar={aplicarFiltro}
        onLimpar={() => {
          limparFiltros();
        }}
        etapas={etapasParaFiltro}
        responsaveis={responsaveis}
      />

      {podeConfigurar && (
        <ConfigurarPipelinePanel
          open={configAberto}
          onClose={() => setConfigAberto(false)}
          pipeline={pipeline}
          formulariosPorEtapa={formulariosPorEtapa}
          onSalvar={handleSalvarPipeline}
          salvando={salvandoConfig}
        />
      )}

      {novoLead && (
        <SlideOverPanel
          open={Boolean(novoLead)}
          onClose={() => setNovoLead(null)}
          title="Novo lead"
          subtitle={
            novoLead?.etapa?.nome
              ? `Será criado na etapa ${novoLead.etapa.nome}`
              : "Capture o contato e o que você já sabe do negócio"
          }
          width="min(95vw, 540px)"
        >
          <NovoLeadForm
            onSubmit={handleCriarLead}
            onCancel={() => setNovoLead(null)}
            loading={salvandoLead}
            etapaNome={novoLead?.etapa?.nome}
          />
        </SlideOverPanel>
      )}
    </div>
  );
}

// ---------- Modo Tabela (lista flat simples) ----------

function CrmTabela({ colunas, loading, onAbrirLead, etapas = [], onMoverEtapa, podeMover }) {
  const linhas = useMemo(() => {
    const out = [];
    colunas.forEach((col) => {
      (col.leads || []).forEach((l) =>
        out.push({ ...l, _etapaNome: col.etapa?.nome || "", _etapaId: col.etapa?.id })
      );
    });
    return out;
  }, [colunas]);

  if (loading && linhas.length === 0) {
    return (
      <TableWrapper>
        <div
          aria-busy="true"
          aria-label="Carregando"
          style={{ display: "flex", flexDirection: "column", gap: 8, padding: 4 }}
        >
          {Array.from({ length: 7 }).map((_, i) => (
            <SkeletonBar key={i} $r={8} style={{ height: 40 }} />
          ))}
        </div>
      </TableWrapper>
    );
  }
  if (linhas.length === 0) {
    return (
      <Card>
        <EmptyMsg>Nenhum lead no pipeline. Use &quot;Novo lead&quot; para comecar.</EmptyMsg>
      </Card>
    );
  }

  return (
    <TableWrapper>
      <Table $rowsClickable>
        <thead>
          <tr>
            <th style={{ width: "12%" }}>Número</th>
            <th style={{ width: "26%" }}>Título</th>
            <th style={{ width: "16%" }}>Etapa</th>
            <th style={{ width: "20%" }}>Contato / cliente</th>
            <th style={{ width: "14%" }}>Responsável</th>
            <th style={{ width: "12%", textAlign: "right" }}>Valor</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr
              key={l.id}
              onClick={() => onAbrirLead(l)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onAbrirLead(l)}
            >
              <td style={{ fontFamily: "var(--font-mono)" }}>{l.numero}</td>
              <td>{l.titulo}</td>
              <td onClick={(e) => e.stopPropagation()}>
                {podeMover && etapas.length > 0 ? (
                  <select
                    value={l._etapaId || ""}
                    onChange={(e) => onMoverEtapa?.(l, e.target.value)}
                    aria-label="Mudar etapa do lead"
                    style={{
                      width: "100%",
                      padding: "4px 6px",
                      borderRadius: 6,
                      border: "1px solid var(--border)",
                      background: "var(--surface)",
                      color: "var(--ink)",
                      font: "inherit",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    {etapas.map((et) => (
                      <option key={et.id} value={et.id}>
                        {et.nome}
                      </option>
                    ))}
                  </select>
                ) : (
                  l._etapaNome
                )}
              </td>
              <td>{l.contato_nome || l.cliente_nome || "—"}</td>
              <td>{l.responsavel_nome || "—"}</td>
              <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>
                {fmtMoeda(l.valor_estimado)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
}

export default CrmBoard;
